import * as React from 'react'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import CssBaseline from '@mui/material/CssBaseline'
import IconButton from '@mui/material/IconButton'
import { Outlet } from 'react-router-dom'
import {
  Button,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { styled, Theme, CSSObject } from '@mui/material/styles'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import MuiDrawer from '@mui/material/Drawer'
import { AuthContext } from '../../core/context/AuthContext'
import { grey } from '@mui/material/colors'
import DrawerHeader from '../DrawerHeader'
import { HtmlTooltip } from '../../components/Tooltip/HtmlTooltip'
import { ITenant, TTenantSettings } from '../../core/types/Tenant'
import Hosts from '../../core/functions/hosts'
import CopiedSnackbar from '../../components/Snackbar/CopiedSnackbar'
import { RWebShare } from 'react-web-share'
import { IconCopy, IconShare } from '../../core/constants/IconConsts'
import UserMenu from '../UserMenu'

const drawerWidth = 240

interface Props {
  window?: () => Window
  drawer: JSX.Element
  open: boolean
  setOpen: (newOpen: boolean) => void
  tenant?: ITenant | null
  tenantSettings?: TTenantSettings | null
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  backgroundColor: grey[50],
  color: '#707275',
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

export default function DesktopLayout(props: Props) {
  const { drawer, open, tenant, tenantSettings, setOpen } = props
  const { user, session } = React.useContext(AuthContext)

  const [copiedLink, setCopiedLink] = React.useState(false)

  const recruitingUrl = `${process.env.REACT_APP_PUBLIC_HOST}/signup?inid=${tenant?.token}`

  const action = (
    <React.Fragment>
      <Button
        color="secondary"
        size="small"
        onClick={() => Hosts.openNewBlank(recruitingUrl)}
        sx={{ color: 'white' }}
      >
        ABRIR LINK
      </Button>
    </React.Fragment>
  )

  const handleChangeCopyURL = async () => {
    await Hosts.copyTextToClipboard(recruitingUrl)
    setCopiedLink(true)
  }

  const handleMouseEnter = () => {
    setOpen(true)
  }

  const handleMouseLeave = () => {
    setOpen(false)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" elevation={0} open={open}>
        <Toolbar>
          <Box paddingBottom="2" paddingTop="2" sx={{ flexGrow: 1 }}>
            <Stack direction="row" justifyContent="end" alignItems="center">
              {tenant &&
                tenantSettings?.acceptIndicationUseTerms &&
                tenantSettings.disableIndicatedComissions === false && (
                  <Box
                    width="310px"
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      px: '12px',
                      py: '6px',
                    }}
                  >
                    <TextField
                      fullWidth
                      variant="standard"
                      size="small"
                      value={recruitingUrl}
                      aria-readonly
                      sx={{
                        gap: '12px',
                        fontStyle: '#707275',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '18px',
                      }}
                      InputProps={{
                        disableUnderline: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <HtmlTooltip
                              title={<Typography>Copiar URL</Typography>}
                            >
                              <IconButton
                                size="small"
                                onClick={handleChangeCopyURL}
                              >
                                <img src={IconCopy} alt="copy" />
                              </IconButton>
                            </HtmlTooltip>
                            <HtmlTooltip
                              title={<Typography>Compartilhar URL</Typography>}
                            >
                              <RWebShare
                                data={{
                                  text: 'Convido você a fazer parte da maior plataforma para afiliados',
                                  url: recruitingUrl,
                                  title: 'Celetus',
                                }}
                                onClick={() =>
                                  console.log('shared successfully!')
                                }
                              >
                                <IconButton
                                  size="small"
                                  aria-label="account of current user"
                                  aria-controls="menu-appbar"
                                  aria-haspopup="true"
                                  sx={{ ml: 0.5 }}
                                >
                                  <img src={IconShare} alt="" />
                                </IconButton>
                              </RWebShare>
                            </HtmlTooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              {/* <Stack direction="row" alignItems="center" marginLeft="5px">
                                <IconFilterButton
                                    icon={<img src="/assets/icons/bell/bell.svg" alt="Noticações" />}
                                    backgroundColor="white"
                                    onClick={handleChangeCopyURL} //provisório
                                />
                            </Stack> */}
            </Stack>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center">
            <UserMenu />
            <Box>
              <Typography fontSize="16px" fontWeight="600" lineHeight="20px">
                {user?.unique_name}
              </Typography>
              <Typography
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                color="#1C52BD"
              >
                {session?.name}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            boxSizing: 'border-box',
            backgroundColor: '#FFFFFF',
            borderColor: '#BCC1FF',
          },
        }}
      >
        <DrawerHeader>
          <Box width="100%" height="100%" sx={{ pt: 1.5, pl: !open ? 0.6 : 0 }}>
            {open && (
              <img
                src="/assets/logo/loginLogo.svg"
                alt="Logo"
                style={{
                  width: '100%',
                  height: '32px',
                  objectFit: 'contain',
                  objectPosition: 'center',
                }}
              />
            )}
            {!open && (
              <img
                src="/assets/logo/icon.svg"
                alt="Icone"
                style={{
                  height: '32px',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
        </DrawerHeader>
        <Divider sx={{ borderColor: '#BCC1FF' }} />
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {drawer}
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>

      <CopiedSnackbar
        open={copiedLink}
        onClose={() => setCopiedLink(false)}
        action={action}
      />
    </Box>
  )
}
